import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { message, Tooltip } from 'antd'
import { Icon } from '@iconify/react'
import { TbLoader2 } from 'react-icons/tb'

// components
import JobInfoDrawer from './JobInfoDrawer'
import JobApplications from './JobApplications'
import AddEditJob from '../../jobs/components/AddEditJob'
import BackAndEdit from '../../../components/backAndEdit'

// store
import {
    getJobDetails,
    jobState,
    switchJobModal,
} from '../../jobs/store/job.slice'
import {
    changePage,
    getJobApplicantListByStatus,
    getStatusWiseCount,
    jobApplicantState,
} from '../store/jobApplicant.slice'
import { loginState } from '../../login/login.slice'
import { getTalentAccess } from '../../../helpers/reportsPermission'

// constants
import { JOB_APPLICATIONS_STATUS } from '../../../constants/jobs'
import { apiJobUpdate } from '../../jobs/store/job.service'
import { PRIVATE_ROUTES } from '../../../routes'

import styles from '../jobApplications.module.css'

export const initialState = {
    jobStatus: 'matches',
    jobIndex: 0,
    selectedApplicantData: null,
    selectedApplicantIndex: null,
    jobStatusList: [],
    jobInfoDrawer: false,
    jobApplicantResumeDrawer: false,
    jobApplicantNotesDrawer: false,
}

const JobDetailsBanner = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const params = useParams()

    const [state, setState] = useState(initialState)

    const { userProfile } = useSelector(loginState)
    const talentAccess = useMemo(
        () => getTalentAccess(userProfile),
        [userProfile]
    )

    const { modalVariations, jobDetails, jobLoading } = useSelector(jobState)
    const jobApplicantStateData = useSelector(jobApplicantState)

    const handleBack = () => {
        dispatch(
            switchJobModal({
                ...modalVariations,
                open: false,
                isEdit: false,
                selectedJobID: null,
            })
        )
        navigate(PRIVATE_ROUTES.jobs)
    }

    const handleEditJobDetails = () => {
        if (!talentAccess.canEdit) return
        if (params.id) {
            dispatch(
                switchJobModal({
                    ...modalVariations,
                    open: true,
                    isEdit: true,
                    selectedJobID: params.id,
                })
            )
        }
    }

    const handleArchive = async () => {
        if (!talentAccess.canDelete) return
        try {
            const data = {
                isArchived: true,
            }

            let response = null

            const payload = {
                updatedData: data,
                _id: params.id,
            }

            response = await apiJobUpdate(payload)

            if (response?.data?.success) {
                message.success(response?.data?.message)

                navigate(PRIVATE_ROUTES.jobs)
            } else {
                message.error(response?.data?.message)
            }
        } catch (error) {
            console.log('error ====>', error)
        }
    }

    const handleSelectApplicationStatus = (status, index) => {
        setState((prevState) => ({
            ...prevState,
            jobStatus: status.key,
            jobIndex: index,
            selectedApplicantData: null,
            selectedApplicantIndex: null,
        }))

        const pageLimit = jobApplicantStateData?.applicantPageLimit || 15

        dispatch(
            changePage({
                currentPage: 1,
                skip: 0,
                pageLimit,
            })
        )

        dispatch(
            getJobApplicantListByStatus({
                skip: 0,
                limit: pageLimit,
                filter: { status: status.key, job: params.id },
            })
        )
    }

    // const handleViewJobInfo = () => {
    //     setState((prevState) => ({
    //         ...prevState,
    //         jobInfoDrawer: true,
    //     }))
    // }

    useEffect(() => {
        if (params.id) {
            const pageLimit = 15
            dispatch(getJobDetails(params.id))
            dispatch(getStatusWiseCount({ jobId: params.id }))
            dispatch(
                changePage({
                    currentPage: 1,
                    skip: 0,
                    pageLimit,
                })
            )
            dispatch(
                getJobApplicantListByStatus({
                    skip: 0,
                    limit: pageLimit,
                    filter: { status: 'matches', job: params.id },
                })
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id])

    useEffect(() => {
        let updatedList = []
        let jobApplicantionsStatus = JOB_APPLICATIONS_STATUS

        if (jobApplicantStateData?.statusList?.length > 0) {
            jobApplicantionsStatus.forEach((status) => {
                const foundItem = jobApplicantStateData?.statusList.find(
                    (item) => status?.value === item?.status
                )
                const value = foundItem ? foundItem.count : 0
                updatedList.push({
                    name: status?.label,
                    value,
                    key: status?.value,
                })
            })
        }

        if (jobApplicantStateData?.statusList?.length === 0) {
            jobApplicantionsStatus.map((status) => {
                updatedList.push({
                    name: status?.label,
                    value: 0,
                    key: status?.value,
                })
            })
        }

        setState((prevState) => ({
            ...prevState,
            jobStatusList: updatedList,
        }))
    }, [jobApplicantStateData])

    useEffect(() => {
        if (jobApplicantStateData?.loaders?.applicantLoading) {
            setState((prevState) => ({
                ...prevState,
                selectedApplicantData: null,
                selectedApplicantIndex: null,
            }))
        }
    }, [jobApplicantStateData?.loaders?.applicantLoading])

    const isApplicantInitialLoading =
        Boolean(jobApplicantStateData?.loaders?.applicantLoading) &&
        !(jobApplicantStateData?.applicantList?.length > 0)

    // One full-page spinner until this job finishes its first load
    const [isBootstrapping, setIsBootstrapping] = useState(true)
    const sawLoadingRef = useRef(false)

    useEffect(() => {
        setIsBootstrapping(true)
        sawLoadingRef.current = false
    }, [params.id])

    useEffect(() => {
        if (jobLoading || isApplicantInitialLoading) {
            sawLoadingRef.current = true
            return
        }

        const jobDetailsMatch = jobDetails?._id === params.id
        if (sawLoadingRef.current || jobDetailsMatch) {
            setIsBootstrapping(false)
        }
    }, [
        jobLoading,
        isApplicantInitialLoading,
        jobDetails?._id,
        params.id,
    ])

    return (
        <>
            {isBootstrapping ? (
                <div className={styles.centeredState}>
                    <TbLoader2
                        size={30}
                        className="spin-loader-resume"
                        color="#4f46e5"
                    />
                </div>
            ) : (
                <>
                    <div className={styles.chrome}>
                        <div className={styles.detailHeader}>
                            <div className={styles.detailHeaderLeft}>
                                <Tooltip title="Back">
                                    <button
                                        type="button"
                                        className={styles.backBtn}
                                        onClick={handleBack}
                                        aria-label="Back to jobs"
                                    >
                                        <Icon
                                            icon="mdi:arrow-left"
                                            width={18}
                                            height={18}
                                        />
                                    </button>
                                </Tooltip>
                                <div className={styles.detailHeaderText}>
                                    <p className={styles.detailTitle}>
                                        {jobDetails?.title || 'Job'}
                                        {jobDetails?.noOfPositions != null
                                            ? ` (${jobDetails.noOfPositions})`
                                            : ''}
                                    </p>
                                    {jobDetails?.location ? (
                                        <p className={styles.detailSubtitle}>
                                            {jobDetails.location}
                                        </p>
                                    ) : null}
                                </div>
                            </div>

                            <div className={styles.detailHeaderActions}>
                                <div className={styles.chipGroup}>
                                    {jobDetails?.status ? (
                                        <span className={styles.statusChip}>
                                            {jobDetails.status}
                                        </span>
                                    ) : null}
                                    <span
                                        className={`${styles.activeChip} ${
                                            jobDetails?.isActiveJob
                                                ? styles.activeChipOn
                                                : styles.activeChipOff
                                        }`}
                                    >
                                        {jobDetails?.isActiveJob
                                            ? 'Active'
                                            : 'Inactive'}
                                    </span>
                                </div>
                                <BackAndEdit
                                    isBack={false}
                                    onEdit={handleEditJobDetails}
                                    state={state}
                                    params={params}
                                    onArchive={handleArchive}
                                    canEdit={talentAccess.canEdit}
                                    canDelete={talentAccess.canDelete}
                                />
                            </div>
                        </div>

                        <div className={styles.viewTabs}>
                            {state.jobStatusList?.map((status, index) => (
                                <button
                                    type="button"
                                    key={status.key || index}
                                    onClick={() =>
                                        handleSelectApplicationStatus(
                                            status,
                                            index
                                        )
                                    }
                                    className={`${styles.viewTab} ${
                                        state.jobIndex === index
                                            ? styles.viewTabActive
                                            : ''
                                    }`}
                                >
                                    <span>{status.name}</span>
                                    <span className={styles.tabCount}>
                                        {status.value}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <JobApplications
                        state={state}
                        setState={setState}
                        canEdit={talentAccess.canEdit}
                    />
                </>
            )}

            {talentAccess.canEdit ? <AddEditJob from="applicant" /> : null}

            <JobInfoDrawer state={state} setState={setState} />
        </>
    )
}

export default JobDetailsBanner
